import React, { useMemo, useState, createElement } from 'react';
import { DownloadIcon, FileSpreadsheetIcon } from 'lucide-react';
import { User, Store, AppView } from '../data/types';
import { Header } from '../components/layout/Header';
import { PageTransition } from '../components/ui/PageTransition';
import { Button } from '../components/ui/Button';
interface WeeklyAllocationPageProps {
  user: User;
  stores: Store[];
  onLogout: () => void;
  onNavigate: (view: AppView) => void;
  activityLog?: any;
}
function getWeekDateRange(weekNumber: number): string {
  const baseDate = new Date('2026-03-03');
  const start = new Date(baseDate);
  start.setDate(baseDate.getDate() + (weekNumber - 1) * 7);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  const fmt = (d: Date) => d.toLocaleDateString('en-ZA', {
    day: 'numeric',
    month: 'short'
  });
  return `${fmt(start)} - ${fmt(end)}`;
}
export function WeeklyAllocationPage({
  user,
  stores,
  onLogout,
  onNavigate,
  activityLog
}: WeeklyAllocationPageProps) {
  // Calculate current week based on April 1, 2026
  const initialWeek = useMemo(() => {
    const baseDate = new Date('2026-03-03');
    const today = new Date('2026-04-01');
    const diffTime = today.getTime() - baseDate.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const week = Math.ceil(diffDays / 7);
    return Math.max(1, Math.min(11, week)); // Cap at 11 so next week (12) exists
  }, []);
  const [selectedWeek, setSelectedWeek] = useState<number>(initialWeek);
  const nextWeek = selectedWeek + 1;
  const getSlotDisplay = (slot?: any) => {
    if (!slot) return {
      text: 'Unknown',
      isInternal: false
    };
    if (slot.status === 'available') return {
      text: 'Available',
      isInternal: false
    };
    if (slot.status === 'blocked') return {
      text: 'Blocked',
      isInternal: false
    };
    if (slot.status === 'internal') {
      return {
        text: slot.internalCampaignName || 'WW Internal',
        isInternal: true
      };
    }
    if (slot.status === 'booked') {
      const isWW = slot.brandName?.startsWith('WW ') || false;
      return {
        text: slot.brandName || 'Booked',
        isInternal: isWW
      };
    }
    return {
      text: slot.status,
      isInternal: false
    };
  };
  const handleDownloadCSV = () => {
    const headers = ['Store List', 'Screen Placement', `This Week (${getWeekDateRange(selectedWeek)})`, `Next Week (${getWeekDateRange(nextWeek)})`];
    const rows: string[][] = [];
    stores.forEach(store => {
      store.positions?.forEach((pos, index) => {
        const slotThisWeek = store.slots.find(s => s.positionId === pos.id && s.weekNumber === selectedWeek);
        const slotNextWeek = store.slots.find(s => s.positionId === pos.id && s.weekNumber === nextWeek);
        const thisWeekDisplay = getSlotDisplay(slotThisWeek).text;
        const nextWeekDisplay = getSlotDisplay(slotNextWeek).text;
        // Only show store name on the first row for this store
        const storeName = index === 0 ? store.name : '';
        rows.push([`"${storeName}"`, `"${pos.name}"`, `"${thisWeekDisplay}"`, `"${nextWeekDisplay}"`]);
      });
    });
    const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `Weekly_Allocation_W${selectedWeek}_W${nextWeek}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  return <div className="min-h-screen w-full bg-white flex flex-col" data-id="element-2088">
      <Header user={user} onLogout={onLogout} currentView="weekly-allocation" onNavigate={onNavigate} stores={stores} activityEntries={activityLog?.entries} unreadCount={activityLog?.unreadCount} onMarkAllRead={activityLog?.markAllAsRead} onMarkRead={activityLog?.markAsRead} data-id="element-2089" />

      <PageTransition className="flex-1 flex flex-col" data-id="element-2090">
        <div className="bg-surface-warm border-b border-border px-6 py-8" data-id="element-2091">
          <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-start justify-between gap-4" data-id="element-2092">
            <div data-id="element-2093">
              <h1 className="text-2xl font-bold text-text-primary mb-2 flex items-center gap-2" data-id="element-2094">
                <FileSpreadsheetIcon className="w-6 h-6 text-primary" data-id="element-2095" />
                Weekly Allocation Report
              </h1>
              <p className="text-text-secondary text-sm" data-id="element-2096">
                Overview of all screen placements across stores for the current
                and upcoming week.
              </p>
            </div>
            <div className="flex items-center gap-4" data-id="element-2097">
              <div className="flex items-center gap-2" data-id="element-2098">
                <label className="text-sm font-medium text-text-secondary" data-id="element-2099">
                  This Week:
                </label>
                <select value={selectedWeek} onChange={e => setSelectedWeek(Number(e.target.value))} className="border border-border rounded-md px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-primary" data-id="element-2100">
                  {Array.from({
                  length: 11
                }, (_, i) => i + 1).map(w => <option key={w} value={w} data-id="element-2101">
                      Week {w} ({getWeekDateRange(w)})
                    </option>)}
                </select>
              </div>
              <Button variant="primary" onClick={handleDownloadCSV} className="flex items-center gap-2" data-id="element-2102">
                <DownloadIcon className="w-4 h-4" data-id="element-2103" />
                Download CSV
              </Button>
            </div>
          </div>
        </div>

        <main className="flex-1 max-w-7xl mx-auto w-full px-6 py-8" data-id="element-2104">
          <div className="bg-white border border-border rounded-lg shadow-sm overflow-hidden overflow-x-auto" data-id="element-2105">
            <table className="w-full text-left border-collapse min-w-[800px]" data-id="element-2106">
              <thead data-id="element-2107">
                <tr data-id="element-2108">
                  <th className="w-1/4 px-4 py-3 text-xs font-bold text-text-primary uppercase tracking-wider border-b border-r border-border bg-surface" data-id="element-2109">
                    STORE LIST
                  </th>
                  <th className="w-1/4 px-4 py-3 text-xs font-bold text-text-primary uppercase tracking-wider border-b border-r border-border bg-surface" data-id="element-2110">
                    SCREEN PLACEMENT
                  </th>
                  <th className="w-1/4 px-4 py-3 border-b border-r border-border bg-surface" data-id="element-2111">
                    <div className="text-xs font-bold text-text-primary uppercase tracking-wider" data-id="element-2112">
                      this week
                    </div>
                    <div className="text-[10px] text-text-secondary font-normal mt-0.5" data-id="element-2113">
                      {getWeekDateRange(selectedWeek)}
                    </div>
                  </th>
                  <th className="w-1/4 px-4 py-3 border-b border-border bg-surface" data-id="element-2114">
                    <div className="text-xs font-bold text-text-primary uppercase tracking-wider" data-id="element-2115">
                      next week
                    </div>
                    <div className="text-[10px] text-text-secondary font-normal mt-0.5" data-id="element-2116">
                      {getWeekDateRange(nextWeek)}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-2117">
                {stores.map(store => {
                return store.positions?.map((pos, index) => {
                  const slotThisWeek = store.slots.find(s => s.positionId === pos.id && s.weekNumber === selectedWeek);
                  const slotNextWeek = store.slots.find(s => s.positionId === pos.id && s.weekNumber === nextWeek);
                  const thisWeek = getSlotDisplay(slotThisWeek);
                  const nextWeekDisplay = getSlotDisplay(slotNextWeek);
                  const isFirstRow = index === 0;
                  // Style matching the reference image
                  const cellClass = 'px-4 py-2 text-xs border-r border-b border-border';
                  const wwGreenClass = 'bg-[#4ade80] text-black font-bold'; // Bright green for WW
                  return <tr key={`${store.id}-${pos.id}`} className="hover:bg-surface/30" data-id="element-2118">
                        <td className={`${cellClass} font-medium ${isFirstRow ? 'text-text-primary' : 'text-transparent select-none'}`} data-id="element-2119">
                          {store.name}
                        </td>
                        <td className={`${cellClass} ${pos.type === 'cylinder' ? 'bg-[#bbf7d0]' : 'bg-[#4ade80]'} text-black`} data-id="element-2120">
                          {pos.name}
                        </td>
                        <td className={`${cellClass} ${thisWeek.isInternal ? wwGreenClass : 'text-text-primary'}`} data-id="element-2121">
                          {thisWeek.text}
                        </td>
                        <td className={`${cellClass} border-r-0 ${nextWeekDisplay.isInternal ? wwGreenClass : 'text-text-primary'}`} data-id="element-2122">
                          {nextWeekDisplay.text}
                        </td>
                      </tr>;
                });
              })}
              </tbody>
            </table>
          </div>
        </main>
      </PageTransition>
    </div>;
}